/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, NavigationExtras } from '@angular/router';
import { UserService } from '../../user/services/user.service';
import { CurrentUserService } from '../../user/services/current-user.service';
import { User } from '../../user/models/user';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationGuardService  {
    constructor(private router: Router, private currentUserService: CurrentUserService, private userService: UserService) { }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {

        // Checks if the current user has access to the current router-path by check the given permissions, usergroups and roles.

        const resolver = (user) => {
            if (route.data && route.data['authenticationData']) {
                const authenticationData = route.data['authenticationData'] as AuthenticationGuardData;
                authenticationData.authenticationType = authenticationData.authenticationType || AuthenticationGuardDataType.Or;

                let hasAccess = true;
                if (user) { // We can only check permissions, roles and usergroups if there is a user

                    let hasAccessPermissions = false;
                    let hasAccessRoles = false;
                    let hasAccessGroups = false;
                    let anythingToCheck = false;

                    // Check permissions...
                    if (authenticationData.requiredPermissions && authenticationData.requiredPermissions.length > 0) {
                        anythingToCheck = true; // If any permissions are set theres only access if the user has any of the permissions
                        if (authenticationData.authenticationType === AuthenticationGuardDataType.Or) {
                            hasAccessPermissions = authenticationData.requiredPermissions.some(p => { return User.hasPermission(p, user); });
                        }
                        else {
                            hasAccessPermissions = authenticationData.requiredPermissions.every(p => { return User.hasPermission(p, user); });
                        }
                    }

                    // Check roles...
                    if (authenticationData.requiredRoles && authenticationData.requiredRoles.length > 0) {
                        anythingToCheck = true; // If any permissions are set theres only access if the user has any of the permissions
                        if (authenticationData.authenticationType === AuthenticationGuardDataType.Or) {
                            hasAccessRoles = authenticationData.requiredRoles.some(r => { return User.hasRole(r, user); });
                        }
                        else {
                            hasAccessRoles = authenticationData.requiredRoles.every(r => { return User.hasRole(r, user); });
                        }
                    }

                    // Check usergroups...
                    if (authenticationData.requiredUserGroups && authenticationData.requiredUserGroups.length > 0) {
                        anythingToCheck = true; // If any permissions are set theres only access if the user has any of the permissions
                        if (authenticationData.authenticationType === AuthenticationGuardDataType.Or) {
                            hasAccessGroups = authenticationData.requiredUserGroups.some(g => { return User.hasUserGroup(g, user); });
                        }
                        else {
                            hasAccessGroups = authenticationData.requiredUserGroups.every(g => { return User.hasUserGroup(g, user); });
                        }
                    }

                    if (anythingToCheck) {
                        hasAccess = hasAccessPermissions || hasAccessRoles || hasAccessGroups;
                    }

                    if (!hasAccess) {
                        //let requiredPermissions = authenticationData.requiredPermissions ? authenticationData.requiredPermissions.join(', ') : null;
                        //let requiredUserGroups = authenticationData.requiredUserGroups ? authenticationData.requiredUserGroups.join(', ') : null;
                        //let requiredRoles = authenticationData.requiredRoles ? authenticationData.requiredRoles.join(', ') : null;

                        //let logMessage = `The user has been denied access to the following path: ${state.url}`;
                        //if (requiredPermissions) {
                        //    logMessage += ` - Required permissions (${requiredPermissions})`;
                        //}
                        //if (requiredUserGroups) {
                        //    logMessage += ` - Required Usergroups (${requiredUserGroups})`;
                        //}
                        //if (requiredRoles) {
                        //    logMessage += ` - Required Roles (${requiredRoles})`;
                        //}

                        // const additionalObject: any = {
                        //     urlParms: route.params,
                        //     authenticationData: authenticationData
                        // };

                        //this.loggingService.logAudit('Authorization_AccessDenied', state.url, additionalObject).then(() => { }).catch(() => { });

                        const extras: NavigationExtras = { replaceUrl: true };
                        if (!hasAccessPermissions) {
                            const separator = authenticationData.authenticationType === AuthenticationGuardDataType.Or ? ' or ' : ' and ';
                            const requiredPermissions = authenticationData.requiredPermissions?.join(separator);
                            extras.state = { error: { status: 403, statusText: 'Forbidden', message: `Access denied; insufficient permissions. Requires at least ${requiredPermissions}.` } };
                        }
                        void this.router.navigate(['/restricted-access'], extras);
                    }
                    else {
                        //this.loggingService.logAudit('Authorization_AccessGranted', state.url).then(() => { }).catch(() => { });
                    }
                }
                else {
                    hasAccess = false; // If no user - no access...
                }

                return hasAccess;
            }
            else {
                //this.loggingService.logAudit('Authorization_AccessGranted', state.url).then(() => { }).catch(() => { });

                return true; // If no authentication-data has been provided - return true.
            }
        };

        const currentUser = this.currentUserService.getCurrentUser();
        if (currentUser) { // If the current user has already been loaded - just invoke the resolver with this user
            return Promise.resolve(resolver(currentUser));
        }
        else {
            // If the current user has not been loaded (eg. on initial load of the app) - get the user from the backend.
            // The Router 'resolve' property is called AFTER canActivate - therefore we need this to prevent deep-links ignoring the canActivate method...
            // https://angular.io/docs/ts/latest/guide/router.html#!#guards
            return this.userService.getCurrentUser().then(loadedUser => {
                return resolver(loadedUser);
            });
        }
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(route, state);
    }
}

export class AuthenticationGuardData {
    requiredPermissions?: string[];
    requiredRoles?: string[];
    requiredUserGroups?: string[];
    authenticationType: AuthenticationGuardDataType = AuthenticationGuardDataType.Or;
}

export enum AuthenticationGuardDataType {
    And = '&',
    Or = '|'
}
